import { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, CheckCircle, XCircle, MinusCircle, Shield, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { useTheme } from '../lib/theme-provider';
import { Mission, MissionStatus, GAME_CONFIGS } from '../types';

interface GameSetup {
  playerCount: number;
  currentRound: number;
  missions: Mission[];
}

const STORAGE_KEY = 'avalon_game_progress';

export default function GameProgress() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [gameStarted, setGameStarted] = useState(false);
  const [playerCount, setPlayerCount] = useState<number>(5);
  const [isLocked, setIsLocked] = useState(false);
  const [game, setGame] = useState<GameSetup | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.gameStarted) {
          setGameStarted(data.gameStarted);
          setPlayerCount(data.playerCount || 5);
          setIsLocked(data.isLocked || false);
          setGame(data.game || null);
        }
      } catch {
        // Invalid data, ignore
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    const data = {
      gameStarted,
      playerCount,
      isLocked,
      game,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [gameStarted, playerCount, isLocked, game]);

  const startGame = () => {
    const config = GAME_CONFIGS[playerCount];
    const missions: Mission[] = config.missions.map((required, index) => ({
      id: index + 1,
      round: index + 1,
      requiredPlayers: required,
      status: 'pending',
      failsRequired: config.failsRequired[index],
    }));

    setGame({
      playerCount,
      currentRound: 1,
      missions,
    });
    setGameStarted(true);
  };

  const resetGame = () => {
    setGameStarted(false);
    setGame(null);
    setIsLocked(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  const updateMissionStatus = (missionId: number, status: MissionStatus) => {
    if (!game) return;

    const updatedMissions = game.missions.map((m) =>
      m.id === missionId ? { ...m, status } : m
    );

    const currentRound = status === 'pending'
      ? missionId
      : Math.min(missionId + 1, 5);

    setGame({
      ...game,
      missions: updatedMissions,
      currentRound,
    });
  };

  const getMissionResult = () => {
    if (!game) return { good: 0, evil: 0 };
    const good = game.missions.filter((m) => m.status === 'success').length;
    const evil = game.missions.filter((m) => m.status === 'fail').length;
    return { good, evil };
  };

  const getGameWinner = () => {
    const { good, evil } = getMissionResult();
    if (good >= 3) return 'good';
    if (evil >= 3) return 'evil';
    return null;
  };

  const winner = getGameWinner();
  const results = getMissionResult();

  if (!gameStarted) {
    return (
      <div className="min-h-screen p-5 pt-[72px] pb-8">
        {/* V189: 固定头部 */}
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-3" style={{ backgroundColor: theme.colors['bg-primary'], borderBottom: `1px solid ${theme.colors['border-secondary'] || '#5A4BFF'}35` }}>
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">游戏进程</h1>
        </div>

        {/* Player Count Selection */}
        <div className="avalon-card-lg">
          <h2 className="text-white font-bold text-lg mb-2">选择游戏人数</h2>
          <p className="text-avalon-text text-sm mb-6">请选择参与游戏的玩家数量（5-13人）</p>

          <div className="grid grid-cols-5 gap-3 mb-4">
            {[5, 6, 7, 8, 9, 10, 11, 12, 13].map((count) => {
              const config = GAME_CONFIGS[count];
              return (
                <button
                  key={count}
                  onClick={() => !isLocked && setPlayerCount(count)}
                  disabled={isLocked}
                  className={`py-4 rounded-xl transition-all ${
                    playerCount === count
                      ? 'bg-avalon-gold text-avalon-bg-dark font-bold'
                      : 'bg-avalon-card text-white hover:bg-avalon-purple'
                  } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs mt-1 opacity-80">
                    {config.good}v{config.evil}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Lock Button */}
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setIsLocked(!isLocked)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all ${
                isLocked
                  ? 'bg-avalon-gold text-avalon-bg-dark font-bold'
                  : 'bg-avalon-card text-white hover:bg-avalon-purple'
              }`}
            >
              <Lock className="w-5 h-5" />
              <span className="text-sm font-medium">{isLocked ? '已锁定' : '锁定'}</span>
            </button>
          </div>

          {/* Preview */}
          <div className="bg-avalon-bg-dark/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-avalon-text text-sm">阵营配置</span>
              <div className="flex gap-2">
                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/30 text-blue-300">
                  正义 {GAME_CONFIGS[playerCount].good}人
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-red-500/30 text-red-300">
                  邪恶 {GAME_CONFIGS[playerCount].evil}人
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {GAME_CONFIGS[playerCount].missions.map((count, index) => (
                <div
                  key={index}
                  className="flex-1 text-center py-2 rounded-lg bg-avalon-purple/30"
                >
                  <div className="text-avalon-gold font-bold text-sm">{count}</div>
                  <div className="text-avalon-text text-xs">第{index + 1}轮</div>
                  {GAME_CONFIGS[playerCount].failsRequired[index] > 1 && (
                    <div className="text-red-400 text-xs">需{GAME_CONFIGS[playerCount].failsRequired[index]}失败</div>
                  )}
                  {index === 3 && (
                    <div className="text-amber-400 text-xs flex items-center justify-center gap-1">
                      <Shield className="w-3 h-3" />
                      保护
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button onClick={startGame} className="w-full avalon-btn py-4 text-lg">
            开始游戏
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemedBackground pageName="game-progress">
    <div className="min-h-screen p-5 pt-[72px] pb-8">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-3" style={{ backgroundColor: theme.colors['bg-primary'], borderBottom: `1px solid ${theme.colors['border-secondary'] || '#5A4BFF'}35` }}>
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">游戏进程</h1>
          <p className="text-avalon-text text-xs">{game?.playerCount}人局</p>
        </div>
        <button
          onClick={resetGame}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white transition-colors"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>

      {/* Score Board */}
      <div className="avalon-card-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">任务进度</h2>
          {winner && (
            <span
              className={`px-3 py-1 rounded-full text-sm font-bold ${
                winner === 'good'
                  ? 'bg-blue-500 text-white'
                  : 'bg-red-500 text-white'
              }`}
            >
              {winner === 'good' ? '正义阵营获胜' : '邪恶阵营获胜'}
            </span>
          )}
        </div>
        <div className="flex gap-4">
          <div className="flex-1 bg-blue-500/20 rounded-xl p-4 text-center border border-blue-500/30">
            <div className="text-blue-400 text-sm mb-1">正义阵营</div>
            <div className="text-3xl font-bold text-blue-400">{results.good}</div>
            <div className="text-blue-400/60 text-xs">胜场</div>
          </div>
          <div className="flex-1 bg-red-500/20 rounded-xl p-4 text-center border border-red-500/30">
            <div className="text-red-400 text-sm mb-1">邪恶阵营</div>
            <div className="text-3xl font-bold text-red-400">{results.evil}</div>
            <div className="text-red-400/60 text-xs">胜场</div>
          </div>
        </div>
      </div>

      {/* Missions */}
      <div className="space-y-4">
        <h2 className="text-white font-bold text-lg">任务记录</h2>
        {game?.missions.map((mission) => (
          <div
            key={mission.id}
            className={`avalon-card transition-all ${
              mission.status !== 'pending' ? 'opacity-80' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    mission.status === 'success'
                      ? 'bg-blue-500'
                      : mission.status === 'fail'
                      ? 'bg-red-500'
                      : 'bg-avalon-purple'
                  }`}
                >
                  {mission.status === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-white" />
                  ) : mission.status === 'fail' ? (
                    <XCircle className="w-6 h-6 text-white" />
                  ) : (
                    <span className="text-white font-bold">{mission.id}</span>
                  )}
                </div>
                <div>
                  <h3 className="text-white font-semibold">第 {mission.id} 轮任务</h3>
                  <p className="text-avalon-text text-xs">
                    需要 {mission.requiredPlayers} 人
                    {mission.failsRequired > 1 && `（需${mission.failsRequired}张失败）`}
                    {mission.id === 4 && (
                      <span className="text-amber-400 ml-1 flex items-center gap-1">
                        <Shield className="w-3 h-3 inline" />
                        保护轮
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {mission.status === 'pending' && !winner && (
                <span className="text-xs px-2 py-1 rounded-full bg-avalon-gold/20 text-avalon-gold">
                  当前
                </span>
              )}
            </div>

            {/* Status Buttons */}
            {mission.status === 'pending' && !winner && (
              <div className="flex gap-2">
                <button
                  onClick={() => updateMissionStatus(mission.id, 'success')}
                  className="flex-1 py-2 px-4 rounded-lg bg-blue-500/30 text-blue-400 hover:bg-blue-500/50 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  成功
                </button>
                <button
                  onClick={() => updateMissionStatus(mission.id, 'fail')}
                  className="flex-1 py-2 px-4 rounded-lg bg-red-500/30 text-red-400 hover:bg-red-500/50 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  失败
                </button>
              </div>
            )}

            {mission.status !== 'pending' && (
              <button
                onClick={() => updateMissionStatus(mission.id, 'pending')}
                className="w-full py-2 px-4 rounded-lg bg-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/50 transition-colors flex items-center justify-center gap-2"
              >
                <MinusCircle className="w-4 h-4" />
                撤销
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Game Over Actions */}
      {winner && (
        <div className="mt-6 avalon-card-lg">
          <h3 className="text-white font-bold text-center mb-4">
            {winner === 'good' ? '🎉 正义阵营获胜！' : '😈 邪恶阵营获胜！'}
          </h3>
          <button onClick={resetGame} className="w-full avalon-btn py-4">
            开始新游戏
          </button>
        </div>
      )}
    </div>
  </ThemedBackground>
  );
}
